import { useLocaleRouter } from "@i18n/routing";
import { usePurchases } from "@saas/payments/hooks/purchases";
import { useConfirmationAlert } from "@saas/shared/components/ConfirmationAlertProvider";
import { apiClient } from "@shared/lib/api-client";
import { downloadWithWatermark } from "@shared/lib/watermark-canvas";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { toast } from "sonner";
import { useImageHomeRouter } from "./image-home";
import { GENERATION_HISTORY_QUERY_KEY, type ImageHistoryItem } from "./types";

/**
 * 没有图片表单的页面（特效页）History 里图片卡的操作，照 /image-to-video 那份：
 * Reprompt / Regenerate / Edit 跳生图页去做，Generate Video 跳 /image-to-video 的 Start Image
 * （10/23 老公定，不往特效页的演员槽里塞）。sessionStorage 的 key 跟 My Assets 一致
 */
export function useImageHistoryActions({
	onDeleted,
}: {
	/** 删掉的图正开着详情弹窗时，调用方把它关掉 */
	onDeleted?: () => void;
} = {}) {
	const tAssets = useTranslations("assets");
	const router = useLocaleRouter();
	const routeImageHome = useImageHomeRouter();
	const queryClient = useQueryClient();
	const { confirm } = useConfirmationAlert();
	const {
		canRemoveWatermark,
		loading: purchasesLoading,
		isError: purchasesError,
	} = usePurchases();
	const [favoriteOverrides, setFavoriteOverrides] = useState<
		Record<string, boolean>
	>({});

	const isImageFavorite = (item: ImageHistoryItem, imageIndex: number) =>
		favoriteOverrides[`${item.id}:${imageIndex}`] ??
		!!item.favoriteImageIndexes?.includes(imageIndex);

	const toggleFavorite = async (
		item: ImageHistoryItem,
		imageIndex: number,
	) => {
		const key = `${item.id}:${imageIndex}`;
		const current = isImageFavorite(item, imageIndex);
		setFavoriteOverrides((previous) => ({ ...previous, [key]: !current }));
		try {
			const response = await (
				apiClient["image-generation"][":id"].favorite.$patch as any
			)({
				param: { id: item.id },
				json: { favorite: !current, imageIndex },
			});
			if (!response.ok) {
				throw new Error("Failed to update favorite");
			}
			queryClient.invalidateQueries({
				queryKey: GENERATION_HISTORY_QUERY_KEY,
			});
		} catch (error) {
			setFavoriteOverrides((previous) => ({
				...previous,
				[key]: current,
			}));
			console.error(error);
			toast.error(tAssets("errors.favoriteFailed"));
		}
	};

	const deleteImage = (id: string, imageIndex: number) => {
		confirm({
			title: tAssets("deleteDialog.title"),
			message: tAssets("deleteDialog.description"),
			confirmLabel: tAssets("deleteDialog.confirm"),
			destructive: true,
			onConfirm: async () => {
				try {
					const response = await fetch(
						`/api/image-generation/${id}?imageIndex=${imageIndex}`,
						{ method: "DELETE", credentials: "include" },
					);
					if (!response.ok) {
						throw new Error("Failed to delete");
					}
					// 服务端删图后会把后面的收藏下标整体前移，按旧下标存的乐观状态就错位了
					setFavoriteOverrides((previous) =>
						Object.fromEntries(
							Object.entries(previous).filter(
								([key]) => !key.startsWith(`${id}:`),
							),
						),
					);
					onDeleted?.();
					queryClient.invalidateQueries({
						queryKey: GENERATION_HISTORY_QUERY_KEY,
					});
					toast.success(tAssets("notifications.deleteSuccess"));
				} catch (error) {
					console.error(error);
					toast.error(tAssets("errors.deleteFailed"));
				}
			},
		});
	};

	// 没有去水印权限的账号下载带水印版本；purchases 没拉到时不加，口径同 WatermarkedImage
	const download = (imageUrl: string) => {
		if (!purchasesLoading && !purchasesError && !canRemoveWatermark) {
			downloadWithWatermark(imageUrl).catch(() =>
				toast.error(tAssets("errors.downloadFailed")),
			);
			return;
		}
		window.location.href = `/api/proxy-download?url=${encodeURIComponent(imageUrl)}`;
	};

	const repromptOrRegenerate = (
		item: ImageHistoryItem,
		action: "reprompt" | "regenerate",
	) => {
		// 姿势页出的图回姿势页（image-home.ts），其余照旧去生图页
		if (routeImageHome(item, action)) {
			return;
		}
		if (action === "reprompt") {
			sessionStorage.setItem(
				"repromptData",
				JSON.stringify({
					prompt: item.originalPrompt,
					inputImageUrls: item.inputImageUrls || [],
					inputType: item.inputImageUrls?.length ? "image" : "text",
				}),
			);
		} else {
			sessionStorage.setItem(
				"regenerateData",
				JSON.stringify({ generation: item }),
			);
		}
		router.push(`/ai-edit-image?action=${action}`);
	};

	const edit = (imageUrl: string) => {
		sessionStorage.setItem("editImageData", JSON.stringify({ imageUrl }));
		router.push("/ai-edit-image?action=edit");
	};

	const generateVideo = (imageUrl: string) => {
		sessionStorage.setItem(
			"imageToVideoData",
			JSON.stringify({ imageUrl }),
		);
		router.push("/image-to-video?action=image-to-video");
	};

	return {
		isImageFavorite,
		toggleFavorite,
		deleteImage,
		download,
		repromptOrRegenerate,
		edit,
		generateVideo,
	};
}
